import type API from "@/Api/API";
import type { Duplicated } from "@/types/duplication";
import type { Music, Playlist } from "@/types/library";
import type {
	DashboardStats,
	ExtendedScene,
	Scene,
	SceneItemType,
	ScenePageDto,
	ScenePlaylistDto,
	SceneUpdateDto,
} from "@/types/scene";

export default class Scenes {
	api: API;

	constructor(api: API) {
		this.api = api;
	}

	async getScenes(): Promise<Scene[]> {
		return this.api
			.processRequest("GET", "/scenes")
			.then((res) => res.data);
	}

	async getDashboardStats(): Promise<DashboardStats> {
		return this.api
			.processRequest("GET", "/scenes/dashboard-stats")
			.then((res) => res.data);
	}

	async getScene(id: number): Promise<ExtendedScene> {
		return this.api
			.processRequest("GET", `/scenes/${id}`)
			.then((res) => res.data);
	}

	async getSceneStructure(id: number): Promise<ExtendedScene> {
		return this.api
			.processRequest("GET", `/scenes/${id}/structure`)
			.then((res) => res.data);
	}

	async getScenePage(id: number, pageId: number): Promise<Music[]> {
		return this.api
			.processRequest("GET", `/scenes/${id}/pages/${pageId}`)
			.then((res) => res.data);
	}

	async getScenePlaylist(id: number, playlistId: number): Promise<Playlist> {
		return this.api
			.processRequest("GET", `/scenes/${id}/playlists/${playlistId}`)
			.then((res) => res.data);
	}

	async addScene(name: string): Promise<Scene> {
		return this.api
			.processRequest("POST", "/scenes", {
				data: { name },
			})
			.then((res) => res.data);
	}

	async updateScene(id: number, scene: SceneUpdateDto): Promise<Scene> {
		return this.api
			.processRequest("PUT", `/scenes/${id}`, {
				data: scene,
			})
			.then((res) => res.data);
	}

	async createPage(sceneId: number, name: string): Promise<void> {
		await this.api.processRequest("POST", `/scenes/${sceneId}/pages`, {
			data: { name },
		});
	}

	async updatePage(
		sceneId: number,
		pageId: number,
		data: {
			name?: string;
			transition?: { effect: string; duration: number };
		}
	): Promise<void> {
		await this.api.processRequest(
			"PUT",
			`/scenes/${sceneId}/pages/${pageId}`,
			{
				data,
			}
		);
	}

	async deletePage(sceneId: number, pageId: number): Promise<void> {
		await this.api.processRequest(
			"DELETE",
			`/scenes/${sceneId}/pages/${pageId}`
		);
	}

	async batchReorderPages(
		sceneId: number,
		pageOrders: Array<{ pageId: number; order: number }>
	): Promise<void> {
		await this.api.processRequest(
			"PUT",
			`/scenes/${sceneId}/pages/reorder`,
			{
				data: { pageOrders },
			}
		);
	}

	async movePageItem(
		sceneId: number,
		pageId: number,
		oldIndex: number,
		newIndex: number
	): Promise<void> {
		await this.api.processRequest(
			"PUT",
			`/scenes/${sceneId}/pages/${pageId}/items/swap`,
			{
				data: { oldIndex, newIndex },
			}
		);
	}

	async movePageItemToPage(
		sceneId: number,
		sourcePageId: number,
		itemId: number,
		targetPageId: number
	): Promise<void> {
		await this.api.processRequest(
			"PUT",
			`/scenes/${sceneId}/pages/${sourcePageId}/items/${itemId}/move`,
			{
				data: { targetPageId },
			}
		);
	}

	async createPageItem(
		sceneId: number,
		pageId: number,
		data: {
			musicIds: number[];
			title: string;
			subtitle?: string;
			color?: string;
		}
	): Promise<void> {
		await this.api.processRequest(
			"POST",
			`/scenes/${sceneId}/pages/${pageId}/items`,
			{ data }
		);
	}

	async updatePageItem(
		sceneId: number,
		pageId: number,
		itemId: number,
		data: {
			title?: string;
			subtitle?: string;
			color?: string;
			musicIds?: number[];
		}
	): Promise<void> {
		await this.api.processRequest(
			"PUT",
			`/scenes/${sceneId}/pages/${pageId}/items/${itemId}`,
			{ data }
		);
	}

	async deleteScene(id: number): Promise<void> {
		await this.api.processRequest("DELETE", `/scenes/${id}`);
	}

	async duplicateScene(
		id: number,
		targetUuid?: string
	): Promise<Duplicated<Scene>> {
		return this.api
			.processRequest("POST", `/scenes/${id}/duplicate`, {
				data: { targetUuid },
			})
			.then((res) => res.data);
	}

	async editSceneButton(
		sceneId: number,
		type: SceneItemType,
		dto: ScenePageDto | ScenePlaylistDto
	): Promise<void> {
		await this.api
			.processRequest("POST", `/scenes/${sceneId}/${type}`, {
				data: dto,
			})
			.then((res) => res.data);
	}

	async swapSceneItems(
		sceneId: number,
		type: string,
		oldOrder: number,
		newOrder: number
	): Promise<void> {
		await this.api
			.processRequest("PUT", `/scenes/${sceneId}/${type}/swap`, {
				data: { oldOrder, newOrder },
			})
			.then((res) => res.data);
	}

	async deleteSceneItem(
		sceneId: number,
		type: string,
		itemId: number,
		pageId?: number
	): Promise<void> {
		let url: string;
		if (type === "pages" && pageId !== undefined) {
			url = `/scenes/${sceneId}/pages/${pageId}/items/${itemId}`;
		} else {
			url = `/scenes/${sceneId}/${type}/${itemId}`;
		}
		await this.api.processRequest("DELETE", url);
	}

	async processAudioOperation(
		sceneId: number,
		type: string,
		itemId: number,
		operationType: string
	): Promise<void> {
		await this.api.processRequest(
			"POST",
			`/scenes/${sceneId}/${type}/${itemId}/${operationType}`
		);
	}

	async pinSceneItem(
		sceneId: number,
		item_type: "page" | "playlist",
		item_id: number
	): Promise<ExtendedScene> {
		return this.api
			.processRequest("POST", `/scenes/${sceneId}/pin`, {
				data: { item_type, item_id },
			})
			.then((res) => res.data);
	}

	async repeatSceneItem(
		sceneId: number,
		item_type: "page" | "playlist",
		item_id: number
	): Promise<ExtendedScene> {
		return this.api
			.processRequest("POST", `/scenes/${sceneId}/repeat`, {
				data: { item_type, item_id },
			})
			.then((res) => res.data);
	}

	async shareScene(
		sceneId: number,
		duration?: string
	): Promise<{ token: string; url: string; expiresAt: Date }> {
		return this.api
			.processRequest("POST", `/scenes/${sceneId}/share`, {
				data: duration ? { duration } : undefined,
			})
			.then((res) => res.data);
	}

	async getSceneByToken(token: string): Promise<ExtendedScene> {
		return this.api
			.processRequest("GET", `/scenes/getByLink/${token}`)
			.then((res) => res.data);
	}
}
